/**
 * DataTable Type Definitions
 *
 * Framework-agnostic type system for the reusable DataTable component.
 * Designed for MarketBetter.ai frontend (Next.js + TypeScript + Tailwind).
 */

import { ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';

// ============================================================================
// Column Types
// ============================================================================

type ColumnType =
  | 'text'
  | 'number'
  | 'badge'
  | 'pill'
  | 'toggle'
  | 'avatarText'
  | 'actions'
  | 'expandable'
  | 'custom';

type ColumnAlignment = 'left' | 'center' | 'right';

type ColumnWidth = number | string | 'auto';

export interface ColumnDefinition<TData = any> {
  /**
   * Unique column identifier
   */
  id: string;

  /**
   * Column header label
   */
  label: string;

  /**
   * Optional description (shown in tooltip)
   */
  description?: string;

  /**
   * Data accessor - key path or function
   */
  accessor: keyof TData | ((row: TData) => any);

  /**
   * Column type determines rendering
   */
  type: ColumnType;

  /**
   * Column width (number = px, string = %, 'auto')
   */
  width?: ColumnWidth;

  /**
   * Minimum width in pixels
   */
  minWidth?: number;

  /**
   * Maximum width in pixels
   */
  maxWidth?: number;

  /**
   * Text alignment
   * @default 'left'
   */
  align?: ColumnAlignment;

  /**
   * Truncate long text with ellipsis
   * @default false
   */
  truncate?: boolean;

  /**
   * Control text wrapping behavior
   * - 'wrap': Allow text to wrap to multiple lines
   * - 'nowrap': Prevent wrapping, text stays on single line (may cause horizontal scroll)
   * @default 'nowrap'
   */
  wrap?: 'wrap' | 'nowrap';

  /**
   * Enable sorting for this column
   * @default false
   */
  sortable?: boolean;

  /**
   * Direction used the first time a sortable column is selected.
   * Scores and recency columns generally start descending.
   * @default 'asc'
   */
  defaultSortDirection?: Exclude<SortDirection, null>;

  /**
   * Enable filtering for this column
   * @default false
   */
  filterable?: boolean;

  /**
   * Enable column resizing
   * @default false
   */
  resizable?: boolean;

  /**
   * Stick column to left/right
   */
  sticky?: 'left' | 'right';

  /**
   * Priority for responsive collapse (higher = kept longer)
   * @default 0
   */
  priority?: number;

  /**
   * Custom cell renderer
   */
  renderCell?: (value: any, row: TData, rowIndex: number) => ReactNode;

  /**
   * Custom header renderer
   */
  renderHeader?: (column: ColumnDefinition<TData>) => ReactNode;

  /**
   * Column-specific metadata for cell types
   */
  meta?: ColumnMeta;
}

interface ColumnMeta {
  // Badge/Pill type
  variant?: string;  // Flexible to support all Badge and Pill variants
  icon?: LucideIcon;
  colorMap?: Record<string, any>;  // Flexible to support both string colors and variant types

  // Number type
  format?: 'integer' | 'decimal' | 'currency' | 'percentage';
  locale?: string;
  currency?: string;
  decimals?: number;

  // Toggle type
  onChange?: (row: any, value: boolean) => void | Promise<void>;
  disabled?: (row: any) => boolean;

  // Actions type
  actions?: RowAction[];

  // Avatar type
  primaryKey?: string;
  secondaryKey?: string;
  avatarKey?: string;
  avatarFallbackKey?: string;
}

// ============================================================================
// Row Actions
// ============================================================================

type ActionKind = 'default' | 'primary' | 'destructive';

export interface RowAction<TData = any> {
  /**
   * Action identifier
   */
  id: string;

  /**
   * Action label (static string or dynamic per-row function)
   */
  label: string | ((row: TData) => string);

  /**
   * Icon name (Lucide) - static or dynamic per-row function
   */
  icon?: LucideIcon | ((row: TData) => LucideIcon);

  /**
   * Action styling variant
   * @default 'default'
   */
  kind?: ActionKind;

  /**
   * Show confirmation dialog before executing
   */
  confirm?: boolean | {
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
  };

  /**
   * Disable action for specific rows
   */
  disabled?: (row: TData) => boolean;

  /**
   * Hide action for specific rows (action won't be rendered at all)
   */
  visible?: (row: TData) => boolean;

  /**
   * Tooltip text
   */
  tooltip?: string;

  /**
   * Accessible label
   */
  ariaLabel?: string | ((row: TData) => string);

  /**
   * Action handler
   */
  onClick: (row: TData, rowIndex: number) => void | Promise<void>;

  /**
   * Show in row overflow menu vs inline
   * @default false (inline)
   */
  overflow?: boolean;

  /**
   * Additional CSS class names for the button
   */
  className?: string;

  /**
   * When true, renders only the icon (no label text). aria-label still describes the action.
   */
  iconOnly?: boolean;
}

export interface BulkAction<TData = any> {
  /**
   * Action identifier
   */
  id: string;

  /**
   * Action label
   */
  label: string;

  /**
   * Icon name
   */
  icon?: LucideIcon;

  /**
   * Action variant
   */
  kind?: ActionKind;

  /**
   * Require confirmation
   */
  confirm?: boolean | {
    title: string;
    message: (count: number) => string;
    confirmLabel?: string;
    cancelLabel?: string;
  };

  /**
   * Disable action conditionally
   */
  disabled?: (rows: TData[]) => boolean;

  /**
   * Action handler
   */
  onClick: (rows: TData[], indices: number[]) => void | Promise<void>;
}

// ============================================================================
// Selection
// ============================================================================

type SelectionMode = 'none' | 'single' | 'multiple';

export interface SelectionState {
  /**
   * Selected row indices on the CURRENT page. When `allSelectedAcrossPages`
   * is true these indices represent the rows on the current page that are
   * NOT excluded (in cross-page mode, individual unchecks accumulate into
   * `excludedIds`, not removals from this array).
   */
  selectedIndices: number[];

  /**
   * Header checkbox state for the current page.
   */
  allSelected: boolean;

  /**
   * GH-1855: when true, the user has explicitly opted-in to "select all N
   * across pages" via the inline banner. Consumers should resolve the actual
   * row set server-side using `(source filter | id range) − excludedIds`.
   */
  allSelectedAcrossPages?: boolean;

  /**
   * GH-1855: row key values (stable across pages) that the user explicitly
   * unchecked while in cross-page mode. Only meaningful when
   * `allSelectedAcrossPages` is true.
   */
  excludedIds?: (string | number)[];
}

/**
 * GH-1855: opt-in cross-page selection configuration. Surfaces a "Select all
 * N across pages?" banner once the current page is fully selected and there
 * are more pages worth selecting. Consumers OPT-IN per table — existing
 * consumers stay on the page-only semantics.
 */
export interface CrossPageSelectionConfig<TData = any> {
  /**
   * Enable cross-page selection banner + flag.
   */
  enabled: boolean;

  /**
   * Total row count across all pages (mirrors `pagination.total`; required
   * so the banner can read it even when client mode total is unset).
   */
  total: number;

  /**
   * Optional override for the row identifier used to track exclusions across
   * pages. Defaults to the table's `rowKey`.
   */
  rowId?: keyof TData | ((row: TData) => string | number);

  /**
   * Optional banner label override — `(total) => string`.
   */
  bannerLabel?: (total: number) => string;
}

// ============================================================================
// Sorting & Filtering
// ============================================================================

export type SortDirection = 'asc' | 'desc' | null;

export interface SortState {
  columnId: string;
  direction: SortDirection;
}

export interface FilterState {
  columnId: string;
  value: any;
  operator?: 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'startsWith' | 'endsWith';
}

// ============================================================================
// Pagination
// ============================================================================

type PaginationMode = 'client' | 'server';

export interface PaginationState {
  /**
   * Current page (0-indexed)
   */
  page: number;

  /**
   * Items per page
   */
  pageSize: number;

  /**
   * Total items (server mode)
   */
  total?: number;
}

export interface PaginationCallbacks {
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

// ============================================================================
// Virtualization
// ============================================================================

interface VirtualizationConfig {
  /**
   * Enable virtual scrolling
   * @default false
   */
  enabled: boolean;

  /**
   * Row height in pixels
   * @default 70
   */
  rowHeight?: number;

  /**
   * Number of rows to render outside viewport
   * @default 5
   */
  overscan?: number;
}

// ============================================================================
// Table State
// ============================================================================

type DensityMode = 'comfortable' | 'compact';

type LayoutMode = 'fixed' | 'fluid' | 'hybrid';

interface TableState {
  /**
   * Current sort configuration
   */
  sort?: SortState;

  /**
   * Active filters
   */
  filters: FilterState[];

  /**
   * Column visibility
   */
  columnVisibility: Record<string, boolean>;

  /**
   * Column order (array of column IDs)
   */
  columnOrder: string[];

  /**
   * Column widths
   */
  columnWidths: Record<string, number>;

  /**
   * Pagination state
   */
  pagination: PaginationState;

  /**
   * Selection state
   */
  selection: SelectionState;

  /**
   * Row density
   */
  density: DensityMode;
}

interface TableCallbacks<TData = any> {
  /**
   * Row click handler
   */
  onRowClick?: (row: TData, index: number) => void;

  /**
   * Cell click handler
   */
  onCellClick?: (row: TData, column: ColumnDefinition<TData>, index: number) => void;

  /**
   * Sort change handler
   */
  onSortChange?: (sort: SortState | null) => void;

  /**
   * Filter change handler
   */
  onFilterChange?: (filters: FilterState[]) => void;

  /**
   * Column resize handler
   */
  onColumnResize?: (columnId: string, width: number) => void;

  /**
   * Column reorder handler
   */
  onColumnReorder?: (columnOrder: string[]) => void;

  /**
   * Selection change handler
   */
  onSelectionChange?: (selection: SelectionState) => void;

  /**
   * Pagination change handlers
   */
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;

  /**
   * Error retry handler
   */
  onErrorRetry?: () => void;
}

// ============================================================================
// Table States
// ============================================================================

interface LoadingConfig {
  /**
   * Loading state — shows full skeleton rows (no cached data available)
   */
  isLoading: boolean;

  /**
   * Background refetch in progress — shows subtle top progress bar without destroying rows.
   * Use when keepPreviousData / placeholderData is active and existing rows should remain visible.
   */
  isFetching?: boolean;

  /**
   * Number of skeleton rows to show
   * @default 5
   */
  skeletonCount?: number;

  /**
   * Loading message
   */
  message?: string;
}

interface EmptyConfig {
  /**
   * Empty state icon
   */
  icon?: LucideIcon;

  /**
   * Empty state title
   */
  title?: string;

  /**
   * Empty state description
   */
  description?: string;

  /**
   * CTA button label
   */
  actionLabel?: string;

  /**
   * CTA button handler
   */
  onAction?: () => void;
}

interface ErrorConfig {
  /**
   * Error state active
   */
  hasError: boolean;

  /**
   * Error title
   */
  title?: string;

  /**
   * Error message
   */
  message?: string;

  /**
   * Retry button label
   */
  retryLabel?: string;

  /**
   * Retry handler
   */
  onRetry?: () => void;
}

// ============================================================================
// Analytics
// ============================================================================

export interface AnalyticsEvent {
  /**
   * Event name
   */
  name: string;

  /**
   * Event properties
   */
  properties?: Record<string, any>;

  /**
   * Timestamp
   */
  timestamp: number;
}

export type AnalyticsEventType =
  | 'table.sort'
  | 'table.filter'
  | 'table.paginate'
  | 'table.select'
  | 'table.action.row'
  | 'table.action.bulk'
  | 'table.error.retry'
  | 'table.column.resize'
  | 'table.column.reorder'
  | 'table.density.change';

export interface AnalyticsHook {
  (eventType: AnalyticsEventType, properties?: Record<string, any>): void;
}

// ============================================================================
// Main Props
// ============================================================================

export interface DataTableProps<TData = any> {
  /**
   * Table data
   */
  data: TData[];

  /**
   * Row key accessor (must be unique & stable)
   */
  rowKey: keyof TData | ((row: TData) => string | number);

  /**
   * Column definitions
   */
  columns: ColumnDefinition<TData>[];

  /**
   * Per-row actions
   */
  rowActions?: RowAction<TData>[];

  /**
   * Bulk actions (when rows selected)
   */
  bulkActions?: BulkAction<TData>[];

  /**
   * Selection mode
   * @default 'none'
   */
  selectionMode?: SelectionMode;

  /**
   * Initial table state
   */
  initialState?: Partial<TableState>;

  /**
   * Pagination configuration
   */
  pagination?: {
    mode: PaginationMode;
    pageSize?: number;
    total?: number;
  };

  /**
   * Virtualization configuration
   */
  virtualization?: VirtualizationConfig;

  /**
   * Optional per-row className callback
   */
  getRowClass?: (row: TData) => string | undefined;

  /**
   * Row density
   * @default 'comfortable'
   */
  density?: DensityMode;

  /**
   * Table layout mode
   * @default 'fluid'
   */
  layout?: LayoutMode;

  /**
   * Minimum table width in pixels. When set, the table scrolls horizontally
   * rather than wrapping cells when the container is narrower (e.g. split-panel
   * layout where the right panel steals width). Mirrors HubTable's minWidth prop.
   */
  minWidth?: number;

  /**
   * Sticky header
   * @default true
   */
  stickyHeader?: boolean;

  /**
   * Offset from top for sticky header (in pixels)
   * Useful when table is inside a scrollable container with fixed headers above
   * @default 0
   */
  stickyTopOffset?: number;

  /**
   * Enable sticky action column when table has horizontal scroll
   * @default true
   */
  stickyActions?: boolean;

  /**
   * Draw gridlines between every cell — row borders (bottom) and column
   * borders (right). Matches the "bordered" table look Sunder asked for on
   * /audiences, /campaigns and /audiences/:id.
   * @default false
   */
  bordered?: boolean;

  /**
   * Loading configuration
   */
  loading?: LoadingConfig;

  /**
   * Empty state configuration
   */
  empty?: EmptyConfig;

  /**
   * Error state configuration
   */
  error?: ErrorConfig;

  /**
   * Event callbacks
   */
  callbacks?: TableCallbacks<TData>;

  /**
   * Analytics hook
   */
  analytics?: AnalyticsHook;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Accessible table label
   */
  ariaLabel?: string;

  /**
   * Increment this counter to programmatically clear the internal selection state.
   * Useful when a parent action (e.g. HubSpot sync) should deselect all rows.
   */
  clearSelectionTrigger?: number;

  /**
   * i18n strings override
   */
  i18n?: Partial<typeof defaultI18n>;

  /**
   * RTL mode
   * @default false
   */
  rtl?: boolean;

  /**
   * Expandable rows configuration
   */
  expandable?: {
    /**
     * Render function for expanded content
     */
    renderExpandedRow: (row: TData, rowIndex: number) => ReactNode;

    /**
     * Initially expanded row keys
     */
    defaultExpandedRows?: (string | number)[];

    /**
     * Callback when row expansion changes
     */
    onExpandChange?: (expandedRowKeys: (string | number)[]) => void;
  };

  /**
   * Theme override
   */
  theme?: Partial<DataTableTheme>;

  /**
   * GH-1855: opt-in "select all across pages" configuration. When omitted,
   * the header select-all stays page-only (legacy behavior).
   */
  crossPageSelection?: CrossPageSelectionConfig<TData>;
}

// ============================================================================
// Theme
// ============================================================================

export interface DataTableTheme {
  colors: {
    bg: {
      card: string;
      tableHeader: string;
      row: string;
      rowHover: string;
      rowSelected: string;
      bulkToolbar: string;
    };
    text: {
      primary: string;
      secondary: string;
      muted: string;
      header: string;
    };
    border: {
      default: string;
      bulkToolbar: string;
    };
    pill: {
      active: {
        bg: string;
        text: string;
      };
      error: {
        bg: string;
        text: string;
      };
      warning: {
        bg: string;
        text: string;
      };
      info: {
        bg: string;
        text: string;
      };
    };
    focus: {
      ring: string;
    };
    avatar: {
      fallbackGradientFrom: string;
      fallbackGradientTo: string;
    };
  };
  spacing: {
    cell: {
      x: string;
      y: string;
    };
    header: {
      y: string;
    };
    pill: {
      x: string;
      y: string;
    };
    avatar: {
      size: string;
    };
  };
  radius: {
    card: string;
    pill: string;
  };
  typography: {
    header: {
      size: string;
      weight: string;
      lineHeight: string;
    };
    primary: {
      size: string;
      weight: string;
      lineHeight: string;
    };
    secondary: {
      size: string;
      weight: string;
      lineHeight: string;
    };
    ui: {
      size: string;
      weight: string;
      lineHeight: string;
    };
  };
  heights: {
    header: string;
    row: {
      comfortable: string;
      compact: string;
    };
  };
}

// ============================================================================
// i18n
// ============================================================================

export const defaultI18n = {
  // Selection
  selectAll: 'Select all',
  deselectAll: 'Deselect all',
  selectRow: 'Select row',
  selectedCount: (count: number) => `${count} selected`,

  // Sorting
  sortAscending: 'Sort ascending',
  sortDescending: 'Sort descending',
  clearSort: 'Clear sort',

  // Filtering
  filter: 'Filter',
  clearFilters: 'Clear filters',

  // Pagination
  page: 'Page',
  of: 'of',
  rowsPerPage: 'Rows per page',
  goToFirstPage: 'Go to first page',
  goToPreviousPage: 'Go to previous page',
  goToNextPage: 'Go to next page',
  goToLastPage: 'Go to last page',

  // Actions
  actions: 'Actions',
  moreActions: 'More actions',

  // States
  loading: 'Loading...',
  noData: 'No data available',
  error: 'An error occurred',
  retry: 'Retry',

  // Confirmation
  confirm: 'Confirm',
  cancel: 'Cancel',

  // Accessibility
  table: 'Data table',
  sortedBy: (column: string, direction: string) =>
    `Sorted by ${column} ${direction === 'asc' ? 'ascending' : 'descending'}`,
};
